/**
 * AlertGroupsSection - Displays alerts in the sidebar grouped by preference
 *
 * Refactored to use the generic SidebarResourceSection and GenericResourceGroup components.
 * This is the first section migrated to the new architecture.
 */

import React, { useCallback } from "react";
import { Alert } from "@/models/alert";
import { useRunAlert, useUpdateAlertGroup } from "@/api/alert";
import { useResourceGroups } from "@/api/resource-group";
import { useAlertsStore } from "@/hooks";
import { useAlertGroupBy } from "@/hooks/use-resource-group-by";
import { alertGalleryConfig } from "@/config/resource-gallery-configs";
import { alertSectionConfig } from "@/config/sidebar-section-configs";
import useNotif from "@/hooks/use-notif";
import SidebarResourceSection, {
  SidebarGroupWithItemsRenderProps,
} from "./sidebar-resource-section";
import GenericResourceGroup, { createUpdateItemGroupFn } from "./generic-resource-group";
import AlertItem from "./alert-item";
import { GroupableItem } from "./resource-group-item";

// =============================================================================
// Types
// =============================================================================

interface AlertGroupsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

// Adapter type to make Alert compatible with GroupableItem
type AlertGroupable = Alert & GroupableItem;

// =============================================================================
// Component
// =============================================================================

const AlertGroupsSection: React.FC<AlertGroupsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const { addSuccess, addError } = useNotif();

  // Fetch alerts via store
  const { resources: alerts, refresh: refreshAlerts } = useAlertsStore();

  // Fetch resource groups for alerts
  const { data: resourceGroups = [] } = useResourceGroups("alert");

  // Get groupBy preference from shared hook
  const { groupBy } = useAlertGroupBy();

  // Mutations
  const runAlertMutation = useRunAlert();
  const updateAlertGroupMutation = useUpdateAlertGroup();

  // Create the updateItemGroup function for GenericResourceGroup
  const updateItemGroup = useCallback(
    async (itemId: string, groupId: string) => {
      await updateAlertGroupMutation.mutateAsync({
        group: { id: groupId },
        uriParams: { id: itemId },
      });
    },
    [updateAlertGroupMutation.mutateAsync]
  );

  // Handle run alert
  const handleRunAlert = useCallback(
    (alert: Alert) => {
      runAlertMutation.mutate(
        { uriParams: { id: alert.id } } as Record<string, never> & { uriParams: { id: string } },
        {
          onSuccess: (result) => {
            if (result.ok) {
              addSuccess(result.triggered ? "Alerte déclenchée" : "Condition non remplie");
              refreshAlerts();
            } else {
              addError(result.error || "Erreur lors de l'exécution");
            }
          },
          onError: () => {
            addError("Erreur lors de l'exécution de l'alerte");
          },
        }
      );
    },
    [runAlertMutation, addSuccess, addError, refreshAlerts]
  );

  // Render a single alert item
  const renderAlertItem = useCallback(
    (
      alert: Alert,
      isSelected: boolean,
      orderedIds: string[],
      multiSelectContextMenu?: (e: React.MouseEvent) => void,
      onDropToCreateGroup?: (draggedId: string, targetId: string) => void
    ) => (
      <AlertItem
        key={alert.id}
        alert={alert}
        onRunAlert={handleRunAlert}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={multiSelectContextMenu}
        onDropToCreateGroup={onDropToCreateGroup}
      />
    ),
    [handleRunAlert]
  );

  // Render a group using GenericResourceGroup
  const renderGroup = useCallback(
    (props: SidebarGroupWithItemsRenderProps<Alert>) => (
      <GenericResourceGroup<AlertGroupable>
        key={props.groupKey}
        groupKey={props.groupKey}
        items={props.items as AlertGroupable[]}
        groupBy={props.groupBy}
        resourceType="alert"
        renderItem={renderAlertItem}
        getGroupIcon={alertSectionConfig.getGroupIcon}
        resourceGroups={resourceGroups}
        allItems={alerts as AlertGroupable[]}
        updateItemGroup={updateItemGroup}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [renderAlertItem, resourceGroups, alerts, updateItemGroup]
  );

  // Render simple item (for ungrouped mode)
  const renderItem = useCallback(
    (props: {
      item: Alert;
      isSelected: boolean;
      orderedIds: string[];
      onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
    }) => (
      <AlertItem
        key={props.item.id}
        alert={props.item}
        onRunAlert={handleRunAlert}
        isSelected={props.isSelected}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [handleRunAlert]
  );

  return (
    <SidebarResourceSection<Alert>
      items={alerts}
      config={alertGalleryConfig}
      renderItem={renderItem}
      renderGroup={renderGroup}
      emptyMessage={alertSectionConfig.emptyMessage}
      noResultsMessage={alertSectionConfig.noResultsMessage}
      searchPlaceholder={alertSectionConfig.searchPlaceholder}
      onMultiSelectContextMenu={onMultiSelectContextMenu}
      externalGroupBy={groupBy}
    />
  );
};

AlertGroupsSection.displayName = "AlertGroupsSection";

export default AlertGroupsSection;
